/**
 * CareLogix™ ORACLE — Animation Library™ (additive).
 *
 * Truthfulness Mandate:
 *  - Animations are educational coaching content only. They never prescribe.
 *  - Each item surfaces the exercise-safety pause directive verbatim.
 *  - No diagnosis. No treatment.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const BACKEND =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) ||
  "";

async function _get(p) {
  try { const r = await fetch(`${BACKEND}${p}`); if (!r.ok) return null; return await r.json(); }
  catch { return null; }
}

const ANIMATION_ICON = {
  walking: "🚶", chair_squats: "🪑", wall_pushups: "💪", bird_dog: "🐦",
  stretching: "🤸", tai_chi: "☯", yoga: "🧘", balance_training: "⚖",
  resistance_bands: "🏋", recovery_mobility: "♻", hydration_reminder: "💧",
  medication_reminder: "💊", breathing_exercise: "🌬",
};

const S = {
  page: { minHeight: "100vh", padding: "32px 40px 80px", background: "linear-gradient(160deg, #050b09 0%, #06231b 60%, #050b09 100%)", color: "#dfe3eb", fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap", marginBottom: 22 },
  eyebrow: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.18em", marginBottom: 4 },
  title: { color: "#fde68a", margin: 0, fontSize: 28, fontFamily: "'Cinzel', serif", letterSpacing: "0.02em" },
  sub: { color: "#cbd5e1", marginTop: 8, fontSize: 13, maxWidth: 760, lineHeight: 1.5 },
  link: { color: "#c5a059", textDecoration: "none", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.1em", marginRight: 18 },
  section: { background: "rgba(13,26,15,0.65)", border: "1px solid rgba(245,200,66,0.20)", borderRadius: 14, padding: 18, marginBottom: 20 },
  sectionTitle: { color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 18, margin: 0, marginBottom: 4 },
  sectionSub: { color: "#cbd5e1", fontSize: 12, lineHeight: 1.5, marginBottom: 12, marginTop: 4 },
  features: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 },
  feat: { padding: "5px 10px", border: "1px solid rgba(245,200,66,0.35)", borderRadius: 999, background: "rgba(245,200,66,0.08)", color: "#fde68a", fontFamily: "'Space Mono', monospace", fontSize: 10 },
  card: { background: "rgba(13,26,15,0.92)", border: "1px solid rgba(245,200,66,0.18)", borderRadius: 12, padding: 18, cursor: "pointer", transition: "transform 120ms ease, border-color 120ms ease", textAlign: "center" },
  cardActive: { borderColor: "#fde68a", transform: "translateY(-2px)" },
  emoji: { fontSize: 38, lineHeight: 1 },
  cardTitle: { color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 14, marginTop: 8 },
  detail: { background: "rgba(13,26,15,0.92)", border: "1px solid rgba(245,200,66,0.35)", borderRadius: 12, padding: 18, marginTop: 14 },
  modes: { display: "flex", flexWrap: "wrap", gap: 6 },
  mode: { padding: "4px 9px", border: "1px solid rgba(124,200,96,0.50)", borderRadius: 999, background: "rgba(124,200,96,0.10)", color: "#7AC860", fontFamily: "'Space Mono', monospace", fontSize: 10 },
  compliance: { marginTop: 28, padding: "14px 18px", background: "rgba(252,211,77,0.06)", border: "1px solid rgba(252,211,77,0.30)", borderRadius: 12, color: "#fde68a", fontSize: 12, lineHeight: 1.5 },
};

export default function OracleAnimationsPage() {
  const [manifest, setManifest] = useState(null);
  const [active, setActive]     = useState(null);
  const [detail, setDetail]     = useState(null);
  const _once = useRef(false);

  useEffect(() => {
    if (_once.current) return; _once.current = true;
    _get("/api/oracle/animations/manifest").then(setManifest);
  }, []);

  const loadDetail = useCallback(async (anim) => {
    setActive(anim); setDetail(null);
    const d = await _get(`/api/oracle/animations/${encodeURIComponent(anim.id)}`);
    setDetail(d);
  }, []);

  const animations = (manifest && manifest.animations) || [];
  const features   = (manifest && manifest.features)   || [];

  return (
    <main style={S.page} data-testid="oracle-animations-page">
      <header style={S.header}>
        <div>
          <div style={S.eyebrow}>ORACLE LIFECOMMAND™ · ANIMATION LIBRARY™</div>
          <h1 style={S.title}>LifeCoach Animation Library</h1>
          <p style={S.sub}>
            Accessibility-first cartoon-coach animations, voice-guided steps, slow-motion playback, and large-text /
            high-contrast modes. Educational coaching only — ORACLE never prescribes intensity, duration, or load.
            <strong> Pause immediately if pain, dizziness, or flare symptoms occur.</strong>
          </p>
        </div>
        <div>
          <Link to="/oracle/lifescore" style={S.link} data-testid="link-lifescore">← LifeScore™</Link>
          <Link to="/oracle/wearables" style={S.link} data-testid="link-wearables">Wearables →</Link>
          <Link to="/oracle/lifecommand" style={S.link} data-testid="link-lifecommand">LifeCommand Console →</Link>
        </div>
      </header>

      {features.length > 0 && (
        <section style={S.section} data-testid="animations-features">
          <h2 style={S.sectionTitle}>Accessibility features</h2>
          <p style={S.sectionSub}>Every animation supports the following coaching modes when available.</p>
          <div style={S.features}>
            {features.map((f) => <span key={f} style={S.feat} data-testid={`feature-${f}`}>{f.replace(/_/g, " ")}</span>)}
          </div>
        </section>
      )}

      <section style={S.section} data-testid="animations-grid">
        <h2 style={S.sectionTitle}>Library ({animations.length})</h2>
        <p style={S.sectionSub}>Tap a card to load its detail.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
          {animations.map((a) => {
            const isActive = active && active.id === a.id;
            const cardStyle = { ...S.card, ...(isActive ? S.cardActive : {}) };
            return (
              <button
                key={a.id}
                type="button"
                style={cardStyle}
                onClick={() => loadDetail(a)}
                data-testid={`animation-${a.id}`}
              >
                <div style={S.emoji}>{ANIMATION_ICON[a.id] || "▶"}</div>
                <div style={S.cardTitle}>{a.name}</div>
              </button>
            );
          })}
        </div>
      </section>

      {active && (
        <div style={S.detail} data-testid="animation-detail">
          <h2 style={{ ...S.sectionTitle, marginBottom: 6 }}>{ANIMATION_ICON[active.id] || "▶"} {active.name}</h2>
          {detail ? (
            <>
              <div style={S.modes}>
                {(detail.modes || []).map((m) => <span key={m} style={S.mode} data-testid={`mode-${m}`}>{m.replace(/_/g, " ")}</span>)}
              </div>
              <p style={{ color: "#cbd5e1", fontSize: 12, marginTop: 12 }}>
                ORACLE renders this exercise via the LOGIX-2 LifeCoach overlay — cartoon coach + voice guidance + slow-motion + step-by-step transcripts.
                Choose a mode in the LifeCommand console to launch the live render.
              </p>
              <p style={{ color: "#fcd34d", fontSize: 12, marginTop: 10, fontFamily: "'Space Mono', monospace" }} data-testid="safety">
                ⚠ {detail.exercise_safety}
              </p>
            </>
          ) : (
            <p style={{ color: "#94a3b8", fontSize: 12 }}>Loading detail…</p>
          )}
        </div>
      )}

      <div style={S.compliance} data-testid="animations-compliance">
        {(manifest && manifest.disclaimer) || "Decision-support only. Not medical advice. Pause if symptoms occur."}
      </div>
    </main>
  );
}
